import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import Nav from "@/components/Nav";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { DASH } from "@/constants/testIds";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer,
} from "recharts";

const bandColor = {
  "Ground Systems": "#A1A1AA",
  "Orbit Prep": "#E0E0E0",
  "Ascent": "#FF9933",
  "Launch-Ready": "#FF9933",
};

export default function Dashboard() {
  const { user, loading } = useAuth();
  const [report, setReport] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await api.latest();
        setReport(r);
        setState("has-report");
      } catch {
        setState("empty");
      }
    })();
  }, [user]);

  if (loading) return <div className="min-h-screen bg-black text-white flex items-center justify-center label-xs">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const persona = user.persona || (report && report.persona) || null;

  return (
    <div className="bg-[color:var(--f2-bg)] min-h-screen text-white">
      <Nav />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <div className="label-xs mb-2">Mission Control · Welcome</div>
            <h1 className="font-display uppercase tracking-tighter font-black text-4xl md:text-6xl">
              {user.name?.split(" ")[0] || "Operator"}, <span className="saffron">command deck.</span>
            </h1>
            <div className="mt-3 label-xs">
              {persona ? `Persona · ${persona.toUpperCase()}` : "Persona · UNASSIGNED"}
            </div>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Link to="/assessment" data-testid={DASH.startAssessment} className="btn-crisp btn-primary">
              {state === "has-report" ? "Retake Forte² Score" : "Start Forte² Score →"}
            </Link>
            <Link to="/jobs" className="btn-crisp">Marketplace</Link>
            <Link to="/learning" className="btn-crisp">Learning Paths</Link>
          </div>
        </div>

        {state === "empty" && (
          <EmptyState />
        )}

        {state === "has-report" && report && (
          <ReportView report={report} />
        )}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="hard-border p-12 bg-[color:var(--f2-surface)]">
      <div className="grid grid-cols-12 gap-6 items-center">
        <div className="col-span-12 md:col-span-8">
          <div className="label-xs mb-4">§ First Mission</div>
          <h2 className="font-display uppercase tracking-tight text-3xl md:text-4xl font-black">
            No <span className="saffron">Forte²</span> score on record.
          </h2>
          <p className="mt-4 text-[color:var(--f2-muted)] max-w-lg">
            Take the diagnostic to unlock a personalised, AI-authored career report for India's space economy.
            Takes ~5 minutes.
          </p>
        </div>
        <div className="col-span-12 md:col-span-4 flex md:justify-end">
          <Link to="/assessment" className="btn-crisp btn-primary">Begin Diagnostic →</Link>
        </div>
      </div>
    </div>
  );
}

function ReportView({ report }) {
  const { scores, ai_report, persona } = report;
  const data = Object.entries(scores.dimensions).map(([k, v]) => ({ dim: k, score: v }));
  const band = scores.band;

  return (
    <div className="space-y-6">
      {/* Top KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="hard-border p-8 bg-[color:var(--f2-surface)]">
          <div className="label-xs">Forte² Overall</div>
          <div data-testid={DASH.overallScore} className="font-display text-7xl font-black tracking-tighter saffron mt-4">
            {scores.overall}
          </div>
          <div className="label-xs mt-3">/ 100</div>
        </div>
        <div className="hard-border p-8 bg-[color:var(--f2-surface)]">
          <div className="label-xs">Readiness Band</div>
          <div
            data-testid={DASH.scoreBand}
            className="font-display text-4xl md:text-5xl font-black tracking-tight mt-4 uppercase"
            style={{ color: bandColor[band] || "#FF9933" }}
          >
            {band}
          </div>
          <div className="label-xs mt-3">Persona · {persona.toUpperCase()}</div>
        </div>
        <div className="hard-border p-8 bg-[color:var(--f2-surface)]">
          <div className="label-xs">Radar</div>
          <div data-testid={DASH.radar} className="h-40 mt-2">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={data} outerRadius="80%">
                <PolarGrid stroke="#27272A" />
                <PolarAngleAxis dataKey="dim" tick={{ fill: "#A1A1AA", fontSize: 9 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={false} stroke="#27272A" />
                <Radar dataKey="score" stroke="#FF9933" fill="#FF9933" fillOpacity={0.35} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Full radar + dimensions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="hard-border p-8 bg-[color:var(--f2-surface)]">
          <div className="label-xs mb-4">Dimensional Analysis</div>
          <div className="h-[380px]">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={data} outerRadius="75%">
                <PolarGrid stroke="#27272A" />
                <PolarAngleAxis dataKey="dim" tick={{ fill: "#E0E0E0", fontSize: 11 }} />
                <PolarRadiusAxis domain={[0, 100]} stroke="#27272A" tick={{ fill: "#71717A", fontSize: 10 }} />
                <Radar dataKey="score" stroke="#FF9933" fill="#FF9933" fillOpacity={0.35} strokeWidth={2} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="hard-border p-8 bg-[color:var(--f2-surface)]">
          <div className="label-xs mb-4">Scores by Dimension</div>
          <div className="space-y-4">
            {data.map((d) => (
              <div key={d.dim}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-[color:var(--f2-muted)]">{d.dim}</span>
                  <span className="saffron font-mono">{d.score}</span>
                </div>
                <div className="h-1 bg-[color:var(--f2-border)]">
                  <div className="h-1 bg-[color:var(--f2-saffron)]" style={{ width: `${d.score}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* AI Report */}
      <div className="hard-border p-8 bg-[color:var(--f2-surface)]" data-testid={DASH.aiSummary}>
        <div className="label-xs mb-4">§ AI-generated Report</div>
        <AiReportRender report={ai_report} persona={persona} />
      </div>
    </div>
  );
}

function AiReportRender({ report, persona }) {
  if (!report || typeof report !== "object") {
    return <p className="text-sm text-[color:var(--f2-muted)]">No AI report available.</p>;
  }
  return (
    <div className="space-y-6">
      {report.summary && (
        <p className="text-base leading-relaxed">{report.summary}</p>
      )}

      {report.readiness_verdict && (
        <div>
          <div className="label-xs mb-1">Verdict</div>
          <p className="saffron font-display text-xl">{report.readiness_verdict}</p>
        </div>
      )}

      {report.thesis_fit && (
        <div>
          <div className="label-xs mb-1">Thesis Fit</div>
          <p className="text-sm">{report.thesis_fit}</p>
        </div>
      )}

      {report.diversification_thesis && (
        <div>
          <div className="label-xs mb-1">Diversification Thesis</div>
          <p className="text-sm">{report.diversification_thesis}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Array.isArray(report.strengths) && report.strengths.length > 0 && (
          <ListBlock title="Strengths" items={report.strengths} accent />
        )}
        {Array.isArray(report.gaps) && report.gaps.length > 0 && (
          <ListBlock title="Gaps" items={report.gaps} />
        )}
      </div>

      {Array.isArray(report.recommended_roles) && report.recommended_roles.length > 0 && (
        <div>
          <div className="label-xs mb-3">Recommended Roles</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {report.recommended_roles.map((r, i) => (
              <div key={i} className="hard-border p-4">
                <div className="font-display font-bold text-lg">{r.title}</div>
                <div className="label-xs mb-2">{r.domain}</div>
                <div className="text-xs text-[color:var(--f2-muted)]">{r.why_fit}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {Array.isArray(report.learning_path) && report.learning_path.length > 0 && (
        <div>
          <div className="label-xs mb-3">Learning Path</div>
          <div className="hard-border">
            {report.learning_path.map((m, i) => (
              <div key={i} className={`grid grid-cols-12 gap-2 p-4 text-sm ${i > 0 ? "border-t border-[color:var(--f2-border)]" : ""}`}>
                <div className="col-span-1 label-xs saffron">{String(i + 1).padStart(2, "0")}</div>
                <div className="col-span-6 font-display font-bold">{m.module}</div>
                <div className="col-span-4 text-[color:var(--f2-muted)]">{m.outcome}</div>
                <div className="col-span-1 label-xs text-right">{m.weeks}w</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {Array.isArray(report.priority_actions) && report.priority_actions.length > 0 && (
        <div>
          <div className="label-xs mb-3">Priority Actions</div>
          <div className="hard-border">
            {report.priority_actions.map((a, i) => (
              <div key={i} className={`grid grid-cols-12 gap-2 p-4 text-sm ${i > 0 ? "border-t border-[color:var(--f2-border)]" : ""}`}>
                <div className="col-span-1 label-xs saffron">{String(i + 1).padStart(2, "0")}</div>
                <div className="col-span-6 font-display font-bold">{a.action}</div>
                <div className="col-span-4 text-[color:var(--f2-muted)]">{a.impact}</div>
                <div className="col-span-1 label-xs text-right">{a.weeks}w</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {Array.isArray(report.next_actions) && report.next_actions.length > 0 && (
        <ListBlock title="Next Actions" items={report.next_actions} accent />
      )}
      {Array.isArray(report.investor_signals) && report.investor_signals.length > 0 && (
        <ListBlock title="Investor Signals" items={report.investor_signals} />
      )}
      {Array.isArray(report.sub_sector_priorities) && report.sub_sector_priorities.length > 0 && (
        <ListBlock title="Sub-Sector Priorities" items={report.sub_sector_priorities} />
      )}
      {Array.isArray(report.diligence_focus) && report.diligence_focus.length > 0 && (
        <ListBlock title="Diligence Focus" items={report.diligence_focus} />
      )}
      {Array.isArray(report.workforce_actions) && report.workforce_actions.length > 0 && (
        <ListBlock title="Workforce Actions" items={report.workforce_actions} />
      )}
      {Array.isArray(report.capability_map) && report.capability_map.length > 0 && (
        <div>
          <div className="label-xs mb-3">Capability Map</div>
          <div className="hard-border">
            {report.capability_map.map((c, i) => (
              <div key={i} className={`grid grid-cols-2 p-4 text-sm ${i > 0 ? "border-t border-[color:var(--f2-border)]" : ""}`}>
                <div>{c.existing}</div>
                <div className="saffron">→ {c.space_opportunity}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ListBlock({ title, items, accent }) {
  return (
    <div>
      <div className="label-xs mb-3">{title}</div>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex gap-3 text-sm">
            <span className={accent ? "saffron" : "text-[color:var(--f2-muted)]"}>◆</span>
            <span>{typeof it === "string" ? it : JSON.stringify(it)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
